// src/components/PhotoUploader.js
import React, { useState } from "react";
import { Upload, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

/**
 * PhotoUploader with client-side compression
 *
 * Props:
 *  - fileList, setFileList (optional controlled)
 *  - maxSizeMB (default 10)
 *  - maxDimension (pixels) - largest width/height after resize (default 1280)
 *
 * Notes:
 *  - Uses canvas to resize + toBlob to compress.
 *  - Returns a Promise from beforeUpload resolving to the compressed File so AntD Upload will use it.
 */
export default function PhotoUploader({
  fileList,
  setFileList,
  maxSizeMB = 10,
  maxDimension = 1280,
}) {
  const [internalList, setInternalList] = useState([]);

  // utility: create an Image from file -> Promise<HTMLImageElement>
  const loadImage = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to load image"));
      };
      img.src = url;
    });

  // utility: convert canvas to blob (Promise)
  const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), type, quality);
    });

  // compress: resize and iteratively reduce quality until under maxSizeMB or quality floor
  const compressImageFile = async (file, opts = {}) => {
    const { maxDim = maxDimension, maxMB = maxSizeMB } = opts;
    // early accept: if already small enough
    if (file.size / 1024 / 1024 <= maxMB) return file;

    try {
      const img = await loadImage(file);
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;

      // compute target dimensions preserving aspect ratio
      let tw = iw;
      let th = ih;
      if (Math.max(iw, ih) > maxDim) {
        if (iw >= ih) {
          tw = maxDim;
          th = Math.round((ih / iw) * maxDim);
        } else {
          th = maxDim;
          tw = Math.round((iw / ih) * maxDim);
        }
      }

      // create canvas and draw
      const canvas = document.createElement("canvas");
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext("2d");
      // optional: fill black/white? Usually not necessary for photos
      ctx.drawImage(img, 0, 0, tw, th);

      // prefer output type jpg or webp if original is not png (png often used for transparency)
      const originalType = file.type || "image/jpeg";
      let outputType = "image/jpeg";
      if (originalType === "image/png") {
        // PNGs often don't compress well as JPEG; still convert to JPEG unless alpha present
        outputType = "image/jpeg";
      } else if (originalType === "image/webp") {
        outputType = "image/webp";
      } else if (originalType === "image/svg+xml") {
        // svg: skip canvas compression (can't rasterize vector meaningfully)
        return file;
      }

      // start with a reasonable quality and reduce if necessary
      let quality = 0.90;
      const qualityFloor = 0.50;
      let blob = await canvasToBlob(canvas, outputType, quality);

      // If compression target not met, reduce quality in a loop
      const maxBytes = maxMB * 1024 * 1024;
      let iterations = 0;
      while (blob && blob.size > maxBytes && quality > qualityFloor && iterations < 8) {
        iterations += 1;
        quality -= 0.12; // step down
        if (quality < qualityFloor) quality = qualityFloor;
        blob = await canvasToBlob(canvas, outputType, quality);
      }

      // If final blob is still bigger than original, keep smaller of original or blob
      if (blob && blob.size >= file.size) {
        // keep original file
        return file;
      }

      // create a File from blob to preserve name and lastModified
      const ext = outputType === "image/webp" ? "webp" : "jpg";
      const newName = file.name ? file.name.replace(/\.[^.]+$/, `.${ext}`) : `photo.${ext}`;
      const compressedFile = new File([blob], newName, {
        type: blob.type || outputType,
        lastModified: Date.now(),
      });
      return compressedFile;
    } catch (err) {
      // on error, fallback to original file
      console.warn("Compression failed, using original file:", err);
      return file;
    }
  };

  // beforeUpload hook returns a Promise that resolves to the file AntD should use
  const beforeUpload = async (file) => {
    const isImage = file.type && file.type.startsWith("image/");
    if (!isImage) {
      message.error("Only image files are allowed (JPG, PNG, WEBP, etc.).");
      return Upload.LIST_IGNORE;
    }

    // Provide visual feedback
    const hide = message.loading("Compressing image...", 0);

    try {
      const compressed = await compressImageFile(file, { maxDim: maxDimension, maxMB: maxSizeMB });
      hide(); // hide loading

      if (!compressed) {
        message.error("Image compression failed.");
        return Upload.LIST_IGNORE;
      }

      // If the compressed file is different, we return it — AntD will add it to fileList
      // If unchanged, returning the original file is fine
      return compressed;
    } catch (e) {
      hide();
      message.error("Failed to process image.");
      return Upload.LIST_IGNORE;
    }
  };

  // onChange handler: keep controlled/uncontrolled behavior
  const onChange = ({ fileList: newList }) => {
    setInternalList(newList);
    if (setFileList) setFileList(newList);
  };

  return (
    <Upload
      accept="image/*"
      listType="picture-card"
      beforeUpload={beforeUpload}
      fileList={fileList || internalList}
      onChange={onChange}
      maxCount={1}
    >
      <div>
        <PlusOutlined />
        <div style={{ marginTop: 8 }}>Upload</div>
      </div>
    </Upload>
  );
}
